import {
  BseDto,
  NegotiationRoundDto,
  ProductGroupBidDto,
  ReceivedBseBidDto,
  WinningProductGroupBidsDto,
} from "../../../../../autogen/bff-api";
import { Product, ProductDraft, ProductGroup } from "../../../../../common/types";
import { eventIsAwarded } from "../../../eventIsAwarded";

export const getSumOfBestPrices = ({ groups, bids }: { groups: ProductGroup[]; bids: ReceivedBseBidDto[] }) => {
  return groups.reduce((sum: number, group: ProductGroup) => sum + getSumOfBestGroupPrices({ group, bids }), 0);
};

export const getSumOfBestGroupPrices = ({
  group,
  bids,
}: {
  group: ProductGroup;
  bids: ReceivedBseBidDto[];
}): number => {
  return (
    group.products?.reduce(
      (sum: number, product: Product | ProductDraft) =>
        sum + (getBestProductPrice({ productId: product.id, group, bids }) ?? 0),
      0
    ) ?? 0
  );
};

export const isBestTotalPrice = ({
  bid,
  bids,
  groups,
}: {
  bid: ReceivedBseBidDto;
  bids: ReceivedBseBidDto[];
  groups: ProductGroup[];
}) => {
  const lowestPrice = getLowestTotalPrice({ groups, bids });
  const price = getTotalPrice({ bid, groups });
  return price <= lowestPrice;
};

const getLowestTotalPrice = ({ bids, groups }: { bids: ReceivedBseBidDto[]; groups: ProductGroup[] }): number => {
  return Math.min(...bids.map((bid) => getTotalPrice({ bid, groups })));
};

export const getTotalPrice = ({ bid, groups }: { bid: ReceivedBseBidDto; groups: ProductGroup[] }): number => {
  return (
    bid.products?.reduce(
      (sum, groupBid) =>
        sum +
        (getProductGroupPrice({
          groupBid,
          productGroup: findProductGroup({ groupId: groupBid.productGroupId, groups }),
        }) ?? 0),
      0
    ) ?? 0
  );
};

const findProductGroup = ({ groupId, groups }: { groupId: string; groups: ProductGroup[] }) => {
  const group = groups.find((g) => g.id === groupId);
  return group;
};

export const getProductGroupPrice = ({
  groupBid,
  productGroup,
}: {
  groupBid?: ProductGroupBidDto;
  productGroup?: ProductGroup;
}): number | undefined => {
  if (!productGroup) return 0;
  return groupBid?.productBids.reduce(
    (sum, productBid) =>
      sum + (getTotalProductPrice({ productId: productBid.productId, group: productGroup, bid: groupBid }) ?? 0),
    0
  );
};

export const isBestProductPrice = ({
  bidId,
  productId,
  group,
  bids,
}: {
  bidId: string;
  productId?: string;
  group: ProductGroup;
  bids: ReceivedBseBidDto[];
}) => {
  if (!productId) throw Error("No product id.");
  const lowestPrice = getBestProductPrice({ productId, group, bids });
  const productPrice = getProductPrice({ bidId, productId, group, bids });
  return (productPrice || productPrice === 0) && productPrice <= (lowestPrice ?? 0);
};

export const getBestProductPrice = ({
  productId,
  group,
  bids,
}: {
  productId?: string;
  group: ProductGroup;
  bids: ReceivedBseBidDto[];
}) => {
  if (!productId) return undefined;
  const prices = bids
    .map((bid) =>
      getTotalProductPrice({
        group,
        bid: findProductGroupBid({ groupId: group.id, bid }),
        productId,
      })
    )
    .filter((price) => !!price || price === 0) as number[];
  return prices.length ? Math.min(...prices) : undefined;
};

const getProductPrice = ({
  bidId,
  productId,
  group,
  bids,
}: {
  bidId: string;
  productId: string;
  group: ProductGroup;
  bids: ReceivedBseBidDto[];
}) => {
  const bid = bids.find((b) => b.id === bidId);
  if (!bid) return undefined;
  const groupBid = findProductGroupBid({ groupId: group.id, bid });
  return getTotalProductPrice({ productId, group, bid: groupBid });
};

export const findProductBid = ({
  productId,
  groupId,
  bid,
}: {
  productId?: string;
  groupId: string;
  bid?: ReceivedBseBidDto;
}) => {
  if (!productId) return undefined;
  return findProductGroupBid({ groupId, bid })?.productBids.find((productBid) => productBid.productId === productId);
};

export const findProductGroupBid = ({ groupId, bid }: { groupId: string; bid?: ReceivedBseBidDto }) =>
  bid?.products?.find((groupBid) => groupBid.productGroupId === groupId);

export const getQuotesRequested = (groups: ProductGroup[]): number => {
  return groups.map((group) => group.products?.length).reduce((count, sum) => (count ?? 0) + (sum ?? 0), 0) ?? 0;
};

export const getQuotesDelivered = ({ groups, bids }: { groups: ProductGroup[]; bids: ReceivedBseBidDto[] }): number => {
  return groups.reduce((sum: number, group) => sum + getQuotesDeliveredInGroup({ group, bids }), 0);
};

const getQuotesDeliveredInGroup = ({ group, bids }: { group: ProductGroup; bids: ReceivedBseBidDto[] }): number => {
  return (
    group.products?.reduce(
      (sum: number, product: Product | ProductDraft) =>
        sum + (isQuoteDeliveredForProduct({ groupId: group.id, productId: product.id, bids }) ? 1 : 0),
      0
    ) ?? 0
  );
};

const isQuoteDeliveredForProduct = ({
  groupId,
  productId,
  bids,
}: {
  groupId: string;
  productId?: string;
  bids: ReceivedBseBidDto[];
}): boolean => {
  if (!productId) throw Error("No productId");
  for (const bid of bids) {
    const productBid = findProductBid({ productId, groupId, bid });
    if (productBid) return true;
  }
  return false;
};

export const getQuotesDeliveredInBid = ({ bid }: { bid: ReceivedBseBidDto }) => {
  const quotesByGroup = bid.products?.map((groupBid) => groupBid.productBids.length);
  return quotesByGroup?.reduce((count, sum) => count + sum, 0) ?? 0;
};

export const getQuotesSelectedInBid = ({
  bid,
  selectedBids,
}: {
  bid: ReceivedBseBidDto;
  selectedBids: WinningProductGroupBidsDto[];
}) => {
  const quotesByGroup = selectedBids?.map(
    (groupBid) => groupBid.bids.filter((productBids) => productBids.bids.includes(bid.id)).length
  );
  return quotesByGroup?.reduce((count, sum) => count + sum, 0) ?? 0;
};

export const getSumOfSelectedPrices = ({
  groups,
  bids,
  selectedBids,
}: {
  groups: ProductGroup[];
  bids: ReceivedBseBidDto[];
  selectedBids: WinningProductGroupBidsDto[];
}) => {
  return groups.reduce((sum: number, group) => sum + getSelectedProductGroupPrice({ group, bids, selectedBids }), 0);
};

export const getSumOfSelectedPricesInBid = ({
  groups,
  bid,
  selectedBids,
}: {
  groups: ProductGroup[];
  bid: ReceivedBseBidDto;
  selectedBids: WinningProductGroupBidsDto[];
}) => {
  return groups.reduce(
    (sum: number, group) => sum + getSelectedProductGroupPriceInBid({ group, bid, selectedBids }),
    0
  );
};

export const getSelectedProductGroupPrice = ({
  group,
  bids,
  selectedBids,
}: {
  group: ProductGroup;
  bids: ReceivedBseBidDto[];
  selectedBids: WinningProductGroupBidsDto[];
}): number => {
  return (
    group.products?.reduce(
      (sum, product) => sum + (getAwardedProductPrice({ group, bids, selectedBids, productId: product.id }) ?? 0),
      0
    ) ?? 0
  );
};

export const getSelectedProductGroupPriceInBid = ({
  group,
  bid,
  selectedBids,
}: {
  group: ProductGroup;
  bid: ReceivedBseBidDto;
  selectedBids: WinningProductGroupBidsDto[];
}): number => {
  return (
    group.products?.reduce(
      (sum, product) => sum + (getAwardedProductPriceInBid({ group, bid, selectedBids, productId: product.id }) ?? 0),
      0
    ) ?? 0
  );
};

export const getAwardedProductPrice = ({
  group,
  productId,
  bids,
  selectedBids,
}: {
  group: ProductGroup;
  productId?: string;
  bids: ReceivedBseBidDto[];
  selectedBids: WinningProductGroupBidsDto[];
}) => {
  if (!productId) throw Error("No product id");
  const selectedProductBids = selectedBids
    ?.find((groupBid) => groupBid.productGroupId === group.id)
    ?.bids.find((productBid) => productBid.productId === productId);
  const selectedBidsForProduct = bids.filter((b) => selectedProductBids?.bids.includes(b.id));
  return selectedBidsForProduct.reduce((sum, bid) => {
    const groupBid = findProductGroupBid({ groupId: group.id, bid });
    return (
      sum +
      (getTotalProductPrice({
        group,
        bid: groupBid,
        productId,
      }) ?? 0)
    );
  }, 0);
};

export const getAwardedProductPriceInBid = ({
  group,
  productId,
  bid,
  selectedBids,
}: {
  group: ProductGroup;
  productId?: string;
  bid: ReceivedBseBidDto;
  selectedBids: WinningProductGroupBidsDto[];
}) => {
  if (!productId) throw Error("No product id");
  const isSelected = !!selectedBids
    ?.find((groupBid) => groupBid.productGroupId === group.id)
    ?.bids.find((productBid) => productBid.productId === productId && productBid.bids.includes(bid.id));
  if (!isSelected) return 0;
  const groupBid = findProductGroupBid({ groupId: group.id, bid });
  return (
    getTotalProductPrice({
      group,
      bid: groupBid,
      productId,
    }) ?? 0
  );
};

export const getSelectedOrWinningBids = (sourcingEvent: BseDto, negotiationRound?: NegotiationRoundDto) => {
  if (negotiationRound) return negotiationRound.selectedProductBids ?? [];
  return eventIsAwarded(sourcingEvent)
    ? (sourcingEvent.awardedFields?.winningProductBids ?? [])
    : (sourcingEvent.publishedFields?.selectedProductGroupBids ?? []);
};

const findWinningBids = ({
  group,
  productId,
  bids,
  selectedBids,
}: {
  group: ProductGroup;
  productId: string;
  bids: ReceivedBseBidDto[];
  selectedBids: WinningProductGroupBidsDto[];
}) => {
  const winningBids = selectedBids
    ?.find((groupBid) => groupBid.productGroupId === group.id)
    ?.bids.find((productBid) => productBid.productId === productId);
  return bids.filter((b) => winningBids?.bids.includes(b.id));
};

export const isAwardedProduct = ({
  group,
  productId,
  bidId,
  bids,
  selectedBids,
}: {
  group: ProductGroup;
  productId: string;
  bidId: string;
  bids: ReceivedBseBidDto[];
  selectedBids: WinningProductGroupBidsDto[];
}) => {
  return findWinningBids({ group, productId, bids, selectedBids })
    .map((b) => b.id)
    .includes(bidId);
};

export const getAwardedProductSuppliers = ({
  group,
  productId,
  bids,
  selectedBids,
}: {
  group: ProductGroup;
  productId?: string;
  bids: ReceivedBseBidDto[];
  selectedBids: WinningProductGroupBidsDto[];
}): string => {
  if (!productId) throw Error("Product is missing id");
  const winningBids = findWinningBids({ group, productId, bids, selectedBids });
  return winningBids?.map((bid) => bid.owningOrganization.name).join(", ");
};

export const getTotalProductPrice = ({
  productId,
  group,
  bid,
}: {
  productId: string;
  group: ProductGroup;
  bid?: ProductGroupBidDto;
}): number | undefined => {
  const productBid = bid?.productBids.find((b) => b.productId === productId);
  const unitPrice = productBid?.unitPrice.value;
  const quantity = group.products?.find((product) => product.id === productId)?.quantity.value;
  if (unitPrice && quantity) return unitPrice * quantity;
  return unitPrice ?? undefined;
};
